import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * GDPR / UK PECR consent state.
 *
 * Categories:
 *   - necessary   Always granted (functional cookies for the site to work).
 *   - analytics   GA4, Search Console, beacons. Off by default.
 *   - marketing   Meta Pixel, Google Ads, etc. Off by default.
 *
 * We persist a single document in localStorage:
 *   { version, decidedAt, choices: { necessary, analytics, marketing } }
 *
 * Bump CONSENT_VERSION whenever you add a new category or materially change
 * what each category implies — the banner will re-prompt every visitor.
 */
export const CONSENT_VERSION = 1;
export const CONSENT_STORAGE_KEY = 'tmdphysio_consent_v1';
export const CONSENT_TTL_MS = 12 * 30 * 24 * 60 * 60 * 1000; // ~12 months

const DEFAULT_CHOICES = { necessary: true, analytics: false, marketing: false };

const ConsentContext = createContext({
  ready: false,
  hydrated: false,
  decided: false,
  choices: DEFAULT_CHOICES,
  acceptAll: () => {},
  rejectAll: () => {},
  saveCustom: (_partial) => {},
  reopenBanner: () => {},
});

const readStored = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.decidedAt !== 'number') return null;
    if (Date.now() - parsed.decidedAt > CONSENT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const ConsentProvider = ({ children }) => {
  const [hydrated, setHydrated] = useState(false);
  const [decided, setDecided] = useState(false);
  const [choices, setChoices] = useState(DEFAULT_CHOICES);

  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setChoices({ ...DEFAULT_CHOICES, ...(stored.choices || {}) });
      setDecided(true);
    }
    setHydrated(true);
  }, []);

  const persist = useCallback((nextChoices) => {
    const doc = {
      version: CONSENT_VERSION,
      decidedAt: Date.now(),
      choices: { ...DEFAULT_CHOICES, ...nextChoices, necessary: true },
    };
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(doc));
    } catch {
      /* private mode etc. — silently degrade. */
    }
    setChoices(doc.choices);
    setDecided(true);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, analytics: true, marketing: true });
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ necessary: true, analytics: false, marketing: false });
  }, [persist]);

  const saveCustom = useCallback((partial) => {
    persist({ ...DEFAULT_CHOICES, ...choices, ...partial, necessary: true });
  }, [choices, persist]);

  const reopenBanner = useCallback(() => {
    setDecided(false);
  }, []);

  const value = useMemo(() => ({
    ready: hydrated,
    hydrated,
    decided,
    choices,
    acceptAll,
    rejectAll,
    saveCustom,
    reopenBanner,
  }), [hydrated, decided, choices, acceptAll, rejectAll, saveCustom, reopenBanner]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);
